import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import {
    EnedisAccessRightModel,
    EnedisAccessRightProofType,
    EnedisAccessRightStatus,
    EnedisTechnicalDataDefinition,
    EnedisTechnicalDataModel,
} from '../../../enedis.models';
import { filter, Observable } from 'rxjs';
import { EnedisState, UpdateFileProof } from '../../../enedis.state';
import { LegendConfig } from 'src/app/shared/components/map/map.component';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    MapColorsAlt,
} from 'src/app/shared/components/map/map.constants';
import { PathOptions } from 'leaflet';
import { RestService } from 'src/app/core/services/rest.service';
import { AlertService, ButtonColors } from '@ed---interne/ng-uui-components';
import { EnedisService } from '../../../enedis.service';
import { HttpResponse } from '@angular/common/http';
import { User } from 'src/app/core/models/user.model';
import { AuthState } from 'src/app/core/states/auth.state';

@UntilDestroy()
@Component({
    selector: 'app-enedis-meter-general',
    templateUrl: './enedis-meter-general.component.html',
    styleUrls: ['./enedis-meter-general.component.scss'],
})
export class EnedisMeterGeneralComponent {
    public technicalDataDefinition = EnedisTechnicalDataDefinition;
    public loggedUser$: Observable<User | null> = inject(Store).select(AuthState.loggedUser);
    public loggedUser: User | undefined;

    public enedisAccessRight$: Observable<EnedisAccessRightModel> = inject(Store)
        .select(EnedisState.currentAccessRight)
        .pipe(filter(Boolean));

    public isMeterActive = true;

    public accessRight: EnedisAccessRightModel | null = null;
    public isProofFile = false;

    public enedisTechnicalData$: Observable<EnedisTechnicalDataModel> = inject(Store)
        .select(EnedisState.currentAccessRightTechnicalData)
        .pipe(filter(Boolean));
    public technicalData: EnedisTechnicalDataModel | null = null;

    public geometries: any[] | null = null;
    public legendConfig: LegendConfig[] = [
        {
            title: 'Localisation',
            values: [
                {
                    color: MapColorsAlt.paleGreen,
                    value: 'Position la plus probable',
                },
            ],
        },
    ];

    buttonColorsPrimary = ButtonColors.Primary;
    constructor(
        private readonly store: Store,
        private readonly restService: RestService,
        private readonly alertService: AlertService,
        private readonly enedisService: EnedisService,
    ) {
        this.loggedUser$.pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.loggedUser = data;
        });

        this.enedisAccessRight$.pipe(untilDestroyed(this)).subscribe((enedisAccessRight) => {
            this.accessRight = enedisAccessRight;
            this.isProofFile = enedisAccessRight.proofType == EnedisAccessRightProofType.FILE;
            this.isMeterActive = enedisAccessRight.status == EnedisAccessRightStatus.ACTIVE;
        });

        this.enedisTechnicalData$.pipe(untilDestroyed(this)).subscribe((technicalData) => {
            console.log(
                `enedisTechnicalData changed, reload localization to ${this.technicalData?.numeroEtNomVoie} - ${this.technicalData?.codePostal}`,
            );
            this.technicalData = technicalData;
            this.getLocalization();
        });
    }

    async getLocalization() {
        let geo: any;
        if (this.technicalData?.numeroEtNomVoie) {
            geo = await this.restService.geocode(
                this.technicalData?.numeroEtNomVoie,
                this.technicalData?.codePostal,
            );
            this.geometries = [geo.features[0]];
        } else {
            console.log('no numeroEtNomVoie');
        }
    }
    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions {
        let fillColor = MapColorsAlt.paleGreen;

        return {
            ...DefaultPolygonStyle,
            fillColor,
        };
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        return `${feature?.properties?.['properties'].label}`;
    }

    public downloadProofFile(type: 'userToThirdParty' | 'userToConsumer'): void {
        if (!this.accessRight) return;
        try {
            this.enedisService
                .getFileProof(this.accessRight.id, type)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (response: HttpResponse<Blob>) => {
                        const a = document.createElement('a');
                        const objectUrl = URL.createObjectURL(response.body!);
                        a.href = objectUrl;
                        const contentDisposition =
                            response.headers.get('Content-Disposition') || '';
                        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
                        const fileName =
                            fileNameMatch?.[1] ||
                            `consentement-${this.accessRight?.id}-${this.accessRight?.pdlNumber}`;

                        a.download = fileName;
                        a.click();
                        URL.revokeObjectURL(objectUrl);
                    },
                    error: (error) => {
                        console.log(error);
                        this.alertService.error(
                            'Erreur lors du téléchargement',
                            `${error?.message}`,
                        );
                    },
                });
        } catch (error: any) {
            console.log(error);
            this.alertService.error('Erreur lors du téléchargement', `${error?.message}`);
        }
    }

    public onFileUpload(file: File) {
        if (!this.accessRight) return;
        this.store
            .dispatch(new UpdateFileProof({ accessRightId: this.accessRight.id, fileProof: file }))
            .subscribe({
                next: () => {
                    this.alertService.valid(
                        'Modification du fichier de consentement',
                        'le fichier à bien été enregistré',
                    );
                },
                error: (err) => {
                    console.info(err);
                    this.alertService.error(
                        'Modification du fichier de consentement',
                        err?.error ?? 'Une erreur est survenue',
                    );
                },
            });
    }
}
